import { useEffect, useRef, useState } from "react";
import { Send } from "lucide-react";
import { tavernAvatarUrl } from "@/lib/avatars";

const ChatPanel = ({
  messages,
  onSend,
  selfRole,
  selfName,
  testIdPrefix = "chat",
  className = "",
  emptyHint = "Hail thy opponent! Words fade when the horn sounds.",
}) => {
  const [text, setText] = useState("");
  const bottomRef = useRef(null);

  useEffect(() => {
    // Defer until after layout so scrollIntoView uses the final size.
    const id = requestAnimationFrame(() => {
      if (bottomRef.current && typeof bottomRef.current.scrollIntoView === "function") {
        bottomRef.current.scrollIntoView({ block: "end", inline: "nearest" });
      }
    });
    return () => cancelAnimationFrame(id);
  }, [messages]);

  const submit = (e) => {
    if (e) e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) return;
    onSend(trimmed);
    setText("");
  };

  return (
    <div className={`flex flex-col ${className}`} data-testid={`${testIdPrefix}-panel`}>
      <div className="text-[10px] tracking-[0.4em] uppercase text-amber-300 font-display mb-2">
        Pre-Match Parley
      </div>
      <div
        className="space-y-2 mb-3 text-sm font-mono border border-amber-500/20 p-2 bg-black/40"
        data-testid={`${testIdPrefix}-messages`}
      >
        {messages.length === 0 ? (
          <div className="text-amber-100/40 text-xs italic" data-testid={`${testIdPrefix}-empty`}>
            {emptyHint}
          </div>
        ) : (
          messages.map((m, i) => {
            const mine = m.from === selfRole;
            const color = m.from === "host" ? "text-red-300" : "text-blue-300";
            const borderColor = m.from === "host"
              ? "border-red-500/40 bg-red-500/10"
              : "border-blue-500/40 bg-blue-500/10";
            return (
              <div
                key={i}
                className={`flex items-start gap-2 ${mine ? "justify-end" : "justify-start"}`}
                data-testid={`${testIdPrefix}-message-${i}`}
              >
                {!mine && m.avatar && (
                  <img src={tavernAvatarUrl(m.avatar)} alt="" className="w-7 h-8 object-cover rounded-sm border border-red-500/40 bg-stone-800 shrink-0" style={{ filter: "contrast(1.25) saturate(0.7) sepia(0.15) brightness(0.95)" }} />
                )}
                <div className={`max-w-[80%] px-3 py-1.5 border ${borderColor}`}>
                  <div className={`text-[10px] tracking-widest uppercase ${color} font-display`}>
                    {m.name || (m.from === "host" ? "Player 1" : "Player 2")}
                  </div>
                  <div className="text-amber-50 break-words text-left whitespace-pre-wrap">{m.text}</div>
                </div>
                {mine && m.avatar && (
                  <img src={tavernAvatarUrl(m.avatar)} alt="" className="w-7 h-8 object-cover rounded-sm border border-blue-500/40 bg-stone-800 shrink-0" style={{ filter: "contrast(1.25) saturate(0.7) sepia(0.15) brightness(0.95)" }} />
                )}
              </div>
            );
          })
        )}
        <div ref={bottomRef} aria-hidden="true" />
      </div>
      <form onSubmit={submit} className="flex gap-2 items-stretch">
        <input
          className="arcade-input flex-1"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={`Speak as ${selfName || "thee"}…`}
          data-testid={`${testIdPrefix}-input`}
        />
        <button
          type="submit"
          className="arcade-btn !px-4 !py-2 text-xs disabled:opacity-40"
          disabled={!text.trim()}
          data-testid={`${testIdPrefix}-send`}
          aria-label="Send message"
        >
          <Send size={14} />
        </button>
      </form>
    </div>
  );
};

export default ChatPanel;
